/** Isolated RFP Scope Discovery client. Does not call Compliance or Analysis endpoints. */

export const SCOPE_DISCOVERY_TIMEOUT_MS = 900_000;

export function analyzedPagesLabel(job, result) {
  if (["xlsx", "xls"].includes(String(job?.source_format || result?.meta?.file_type || "").toLowerCase())) {
    return (
      result?.meta?.analyzed_sheets_label ||
      result?.meta?.analyzed_pages_label ||
      (job?.selected_sheets === "*"
        ? "All worksheets"
        : Array.isArray(job?.selected_sheets) && job.selected_sheets.length
          ? job.selected_sheets.join(", ")
          : "Selected worksheets")
    );
  }
  const fromResult = result?.meta?.analyzed_pages_label;
  if (fromResult) return fromResult;
  const mode = result?.scope?.document_scope?.analyzed_pages?.mode;
  const pages = result?.scope?.document_scope?.analyzed_pages?.pages || [];
  if (mode === "selected_pages" && pages.length) {
    return formatPageSpan(pages);
  }
  const raw = (job?.page_ranges_raw || "").trim();
  if ((job?.page_scope === "specific_pages" || (Array.isArray(job?.page_ranges) && job.page_ranges.length)) && (raw || job?.page_ranges)) {
    if (raw) return `Pages ${raw.replace(/-/g, "–")}`;
    return formatPageSpan(expandStoredRanges(job.page_ranges));
  }
  return "Entire document";
}

function expandStoredRanges(ranges) {
  const pages = [];
  for (const item of ranges || []) {
    const start = Number(item?.[0]);
    const end = Number(item?.[1]);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    for (let page = start; page <= end; page += 1) pages.push(page);
  }
  return pages;
}

export function formatPageSpan(pages) {
  const unique = [...new Set((pages || []).map(Number).filter((n) => Number.isFinite(n) && n >= 1))].sort(
    (a, b) => a - b
  );
  if (!unique.length) return "Entire document";
  const ranges = [];
  let start = unique[0];
  let prev = unique[0];
  for (let i = 1; i < unique.length; i += 1) {
    if (unique[i] === prev + 1) {
      prev = unique[i];
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = unique[i];
    prev = unique[i];
  }
  ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
  return `Pages ${ranges.join(", ")}`;
}

export function hasSupportedRfpDocument(job) {
  if (!job) return false;
  const format = (job.source_format || "").toLowerCase();
  return Boolean(
    job.artifacts?.pdf ||
      job.artifacts?.docx ||
      job.artifacts?.xlsx ||
      job.artifacts?.xls ||
      format === "pdf" ||
      format === "docx" ||
      format === "xlsx" ||
      format === "xls"
  );
}

/**
 * @param {string} jobId
 * @param {object} options
 * @param {import('axios').AxiosInstance} options.http
 * @param {string} options.apiBase
 */
export async function runScopeDiscovery(jobId, { http, apiBase }) {
  if (!jobId) throw new Error("Job ID is required");
  const res = await http.post(`${apiBase}/rfp-jobs/${jobId}/scope-discovery`, null, {
    timeout: SCOPE_DISCOVERY_TIMEOUT_MS,
  });
  return res.data;
}

export async function loadScopeDiscovery(jobId, { http, apiBase }) {
  if (!jobId) return null;
  const res = await http.get(`${apiBase}/rfp-jobs/${jobId}/scope-discovery`);
  return res.data || null;
}

export async function saveScopeDiscoveryReview(jobId, body, { http, apiBase }) {
  if (!jobId) throw new Error("Job ID is required");
  const res = await http.patch(`${apiBase}/rfp-jobs/${jobId}/scope-discovery/review`, body);
  return res.data;
}

export async function previewProductDecision(jobId, body, { http, apiBase }) {
  if (!jobId) throw new Error("Job ID is required");
  const res = await http.post(`${apiBase}/rfp-jobs/${jobId}/scope-discovery/product-decision/preview`, body);
  return res.data;
}

export async function applyProductDecision(jobId, body, { http, apiBase }) {
  if (!jobId) throw new Error("Job ID is required");
  const res = await http.post(`${apiBase}/rfp-jobs/${jobId}/scope-discovery/product-decision`, body);
  return res.data;
}
